import React, { useEffect } from 'react';

/**
 * Privacy e Cookie Policy vivono su iubenda, non nel nostro codice: il piano
 * Essentials non permette di incorporarne il testo nelle nostre pagine (l'API
 * risponde 403 "upgrade to a higher tier"), quindi le mostriamo nel popup di
 * iubenda o, in mancanza, sulla pagina ospitata da iubenda.
 */
const POLICY_ID = '12884109';
export const IUBENDA_PRIVACY_URL = `https://www.iubenda.com/privacy-policy/${POLICY_ID}`;
export const IUBENDA_COOKIE_URL = `https://www.iubenda.com/privacy-policy/${POLICY_ID}/cookie-policy`;

const LOADER_SRC = 'https://cdn.iubenda.com/iubenda.js';

declare global {
  interface Window {
    _iub?: any;
  }
}

/**
 * Lo script in `<head>` (public/index.html) è sincrono: se `_iub` non c'è
 * quando il bundle parte, iubenda è spento (REACT_APP_IUBENDA_ENABLED=false)
 * o bloccato, e non c'è un pannello preferenze da aprire.
 */
const COOKIE_SOLUTION_LOADED = typeof window !== 'undefined' && Boolean(window._iub?.csConfiguration);

/**
 * `iubenda.js` trasforma in popup i link `.iubenda-embed` presenti nel DOM
 * quando si carica, una volta sola: un link montato dopo resta un link
 * normale verso iubenda, per questo `target="_blank"`.
 */
function loadIubendaLoader() {
  if (document.querySelector(`script[src="${LOADER_SRC}"]`)) return;
  const s = document.createElement('script');
  s.src = LOADER_SRC;
  s.async = true;
  document.body.appendChild(s);
}

/**
 * Link a un documento iubenda che si apre nel popup di iubenda.
 *
 * `iubenda-nostyle` non è cosmetico: con gli stili `white`/`black` iubenda
 * sostituisce l'`<a>` con un iframe, togliendo a React un nodo che gestisce.
 * Con `nostyle` il nodo resta lo stesso e mantiene le nostre classi.
 */
export function IubendaPolicyLink({ href, className = '', children }: { href: string; className?: string; children: React.ReactNode }) {
  useEffect(loadIubendaLoader, []);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`iubenda-nostyle iubenda-noiframe iubenda-embed ${className}`}
    >
      {children}
    </a>
  );
}

/**
 * Riapre il pannello preferenze del banner cookie: il GDPR chiede che il
 * consenso si possa cambiare o revocare in ogni momento.
 */
export function CookiePreferencesLink({ className = '', children }: { className?: string; children: React.ReactNode }) {
  if (!COOKIE_SOLUTION_LOADED) return null;
  return (
    <button
      type="button"
      className={className}
      onClick={() => window._iub?.cs?.api?.openPreferences?.()}
    >
      {children}
    </button>
  );
}

/**
 * `/legale/privacy` e `/legale/cookie` restano indirizzi validi (segnalibri,
 * link esterni) ma il testo è su iubenda: sostituiamo la pagina.
 */
export function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}
